import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { TravelOfferWalletSpendCoverage } from '@graphql/generated-base';
import { getCurrencyDecimalPlaces } from '@shared/utils/currency/minorUnits';
import type { CashFundConfig, CashFundCoverage } from './cashFund.types';

/**
 * How the fund applies to one listing, or `null` when it cannot apply — the single eligibility
 * answer the tile, filter, marker and sort all read, so they cannot drift apart.
 *
 * Every figure comes from the listing's `walletSpend` block. The front end computes no money here.
 */
export const getCashFundCoverage = (listing: AccommodationListingDTO, fund: CashFundConfig | undefined): CashFundCoverage | null => {
  if (!fund) return null;
  // Custom links arrive as 'roomBlock' too — a couple's own rate, not one the fund can pay.
  if (listing.type !== 'standardHotel') return null;
  if (listing.isSoldOutForSelectedDates) return null;

  // Null for a guest, a non-owner, the pay-path switch off, or a tile with no rate. Plain tile.
  const walletSpend = listing.walletSpend;
  if (!walletSpend) return null;

  // The fund pays nothing here, so there is no split to state.
  if (walletSpend.coverage === TravelOfferWalletSpendCoverage.NONE) return null;

  // A mismatch means stale figures mid-currency-switch; fail closed rather than quote a wrong split.
  if (walletSpend.currency !== fund.currency) return null;

  // Until partial payment is allowed, only a fully covered stay may show the fund — today's display.
  if (!walletSpend.partialAllowed && walletSpend.coverage !== TravelOfferWalletSpendCoverage.FULL) return null;

  return {
    fundCoversMinorUnits: walletSpend.fundAppliedMinorUnits,
    outOfPocketMinorUnits: walletSpend.cardDueNowMinorUnits,
    // The block carries no digits, so the currency decides them — JPY has none.
    digits: getCurrencyDecimalPlaces(walletSpend.currency),
    suggestedCoupon: walletSpend.suggestedCoupon ?? null,
  };
};
